/**
 * Pagination - 페이지네이션 컴포넌트
 *
 * @param variant - 'numbered' (기본) | 'dots' | 'compact' 표시 형태
 * @param siblings - 현재 페이지 좌우에 표시할 페이지 번호 개수
 * @param showInfo - true이면 "X-Y / 총 N건" 정보 텍스트 표시
 */
'use client';

import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import styles from './Pagination.module.css';

export interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems?: number;
  pageSize?: number;
  onChange: (page: number) => void;
  variant?: 'numbered' | 'dots' | 'compact';
  size?: 'sm' | 'md';
  showFirst?: boolean;
  showLast?: boolean;
  showInfo?: boolean;
  siblings?: number;
  className?: string;
}

function range(start: number, end: number): number[] {
  const arr: number[] = [];
  for (let i = start; i <= end; i++) arr.push(i);
  return arr;
}

const ELLIPSIS = -1;

function buildPageRange(
  page: number,
  totalPages: number,
  siblings: number,
): (number | typeof ELLIPSIS)[] {
  const totalSlots = siblings * 2 + 5;

  if (totalPages <= totalSlots) {
    return range(1, totalPages);
  }

  const leftSibling = Math.max(page - siblings, 1);
  const rightSibling = Math.min(page + siblings, totalPages);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftCount = 3 + 2 * siblings;
    return [...range(1, leftCount), ELLIPSIS, totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightCount = 3 + 2 * siblings;
    return [1, ELLIPSIS, ...range(totalPages - rightCount + 1, totalPages)];
  }

  return [1, ELLIPSIS, ...range(leftSibling, rightSibling), ELLIPSIS, totalPages];
}

export default function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize = 10,
  onChange,
  variant = 'numbered',
  size = 'md',
  showFirst = false,
  showLast = false,
  showInfo = false,
  siblings = 1,
  className = '',
}: PaginationProps) {
  const pages = useMemo(
    () => buildPageRange(page, totalPages, siblings),
    [page, totalPages, siblings],
  );

  const sizeClass = size === 'sm' ? styles.sm : '';
  const iconSize = size === 'sm' ? 14 : 16;

  // Info text
  const infoText = useMemo(() => {
    if (!showInfo) return null;
    if (totalItems != null) {
      const start = (page - 1) * pageSize + 1;
      const end = Math.min(page * pageSize, totalItems);
      return `${start}-${end} / 총 ${totalItems.toLocaleString()}건`;
    }
    return `${page} / ${totalPages} 페이지`;
  }, [showInfo, totalItems, page, pageSize, totalPages]);

  if (totalPages <= 0) return null;

  // Compact variant: just prev/next + page info
  if (variant === 'compact') {
    return (
      <nav
        aria-label="페이지 탐색"
        className={[styles.pagination, styles.compact, sizeClass, className]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          type="button"
          className={styles.navButton}
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          aria-label="이전 페이지"
        >
          <ChevronLeft size={iconSize} />
        </button>
        <span className={styles.compactInfo}>
          {page} / {totalPages} 페이지
        </span>
        <button
          type="button"
          className={styles.navButton}
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          aria-label="다음 페이지"
        >
          <ChevronRight size={iconSize} />
        </button>
      </nav>
    );
  }

  // Dots variant
  if (variant === 'dots') {
    return (
      <nav
        aria-label="페이지 탐색"
        className={[styles.pagination, styles.dots, sizeClass, className].filter(Boolean).join(' ')}
      >
        <button
          type="button"
          className={styles.navButton}
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          aria-label="이전 페이지"
        >
          <ChevronLeft size={iconSize} />
        </button>
        <div className={styles.dotsContainer}>
          {range(1, totalPages).map((p) => (
            <button
              key={p}
              type="button"
              className={[styles.dot, p === page ? styles.dotActive : ''].filter(Boolean).join(' ')}
              onClick={() => onChange(p)}
              aria-label={`${p}페이지`}
              aria-current={p === page ? 'page' : undefined}
            />
          ))}
        </div>
        <button
          type="button"
          className={styles.navButton}
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          aria-label="다음 페이지"
        >
          <ChevronRight size={iconSize} />
        </button>
      </nav>
    );
  }

  // Numbered variant (default)
  return (
    <nav
      aria-label="페이지 탐색"
      className={[styles.pagination, sizeClass, className].filter(Boolean).join(' ')}
    >
      <div className={styles.pageControls}>
        {showFirst && (
          <button
            type="button"
            className={styles.navButton}
            onClick={() => onChange(1)}
            disabled={page === 1}
            aria-label="첫 페이지"
          >
            <ChevronsLeft size={iconSize} />
          </button>
        )}

        <button
          type="button"
          className={`${styles.navButton} ${styles.prevNext}`}
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          aria-label="이전 페이지"
        >
          <ChevronLeft size={iconSize} />
          <span className={styles.prevNextLabel}>이전</span>
        </button>

        {pages.map((p, i) =>
          p === ELLIPSIS ? (
            <span key={`ellipsis-${i}`} className={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={[styles.pageButton, p === page ? styles.active : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => onChange(p)}
              aria-label={`${p}페이지`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          className={`${styles.navButton} ${styles.prevNext}`}
          onClick={() => onChange(page + 1)}
          disabled={page === totalPages}
          aria-label="다음 페이지"
        >
          <span className={styles.prevNextLabel}>다음</span>
          <ChevronRight size={iconSize} />
        </button>

        {showLast && (
          <button
            type="button"
            className={styles.navButton}
            onClick={() => onChange(totalPages)}
            disabled={page === totalPages}
            aria-label="마지막 페이지"
          >
            <ChevronsRight size={iconSize} />
          </button>
        )}
      </div>

      {infoText && <span className={styles.infoText}>{infoText}</span>}
    </nav>
  );
}
